import { ConnectingAirportsOutlined } from "@mui/icons-material";
import axios from "axios";
import { relativeTimeRounding } from "moment";
 
const PDFResource = {
  /**
   * Converts base64 string to Blob
   * @param {string} base64Data - Base64 encoded string (with or without data URI prefix)
   * @param {string} contentType - MIME type (default: 'application/pdf')
   * @returns {Blob} - Blob object
   */
  base64ToBlob: (base64Data, contentType = 'application/pdf') => {
    
    try {
      // Remove data URI prefix if present (e.g., "data:application/pdf;base64,")
      let base64String = base64Data;
      if (base64Data.includes(',')) {
        base64String = base64Data.split(',')[1];
      }
 
      // Decode base64 string to binary
      const byteCharacters = atob(base64String);
 
      // Create byte array
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
 
      const byteArray = new Uint8Array(byteNumbers);

      let finalContentType = contentType
      if(!finalContentType || finalContentType === '' || finalContentType === 'text/plain' || finalContentType.includes('text/')){
        console.error("Critical: Invalid contentType detected:", finalContentType);
        console.error("This would create .txt file")
        finalContentType = 'application/pdf';
      }
 
      // Create Blob with proper MIME type
      const blob = new Blob([byteArray], { type: contentType });
      
      // Critical check for blob type
      if (blob.type !== contentType) {
        console.error("❌ CRITICAL: Blob type mismatch!");
        console.error("Expected:", contentType);
        console.error("Got:", blob.type);

        const fixedBlob = new Blob([byteArray],{type:finalContentType})
        return fixedBlob
      } 
      if(blob.type === '' || blob.type === 'text/plain'){
        throw new Error ("Failed to create blob with correct type")
      }
      return blob;
    } catch (error) {
      console.error("❌ base64ToBlob ERROR");
      console.error("Error name:", error.name);
      console.error("Error stack:", error.stack);
      throw new Error(`Failed to convert base64 to Blob: ${error.message}`);
    }
  },
 
  /**
   * Check if URL is a data URI (bulletproof version)
   * @param {string} url - URL to check
   * @returns {boolean} - True if data URI
   */
  isDataURI: (url) => {
    
    if (!url) {
      return false;
    }
 
    // Convert to string and trim
    const urlString = String(url).trim();
 
    // Remove quotes if present
    const cleanUrl = urlString.replace(/^["']|["']$/g, '');
 
    const result = cleanUrl.startsWith('data:');
 
    return result;
  },
 
  /**
   * Converts data URI to Blob URL
   * @param {string} dataURI - Data URI string
   * @returns {string} - Blob URL
   */
  dataURIToBlobURL: (dataURI) => {
    
    try {
      // Extract MIME type from data URI
      const mimeMatch = dataURI.match(/data:([^;]+);/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/pdf';
      
      if (!mimeMatch) {
        console.warn("⚠️ No MIME type found in data URI, defaulting to application/pdf");
      }
 
      // Convert to Blob
      const blob = PDFResource.base64ToBlob(dataURI, mimeType);
 
      // Create Blob URL
      const blobUrl = window.URL.createObjectURL(blob);
 
      return blobUrl;
    } catch (error) {
      console.error("❌ dataURIToBlobURL ERROR");
      console.error("Error:", error);
      throw error;
    }
  },
 
  /**
   * Gets file type from URL
   */
  getFileType: (url) => {
    
    const urlLower = url.toLowerCase();
    
    let fileType = 'unknown';
    
    if (urlLower.includes('.xlsx') || urlLower.includes('.xls') || urlLower.includes('.xlsm') || urlLower.includes('spreadsheet') || urlLower.includes('excel')) {
      fileType = 'excel';
    } else if (urlLower.includes('.pdf') || urlLower.includes('application/pdf')) {
      fileType = 'pdf';
    } else {
      console.warn("⚠️ Unknown file type");
    }
    
    return fileType;
  },
 
  /**
   * Downloads PDF and returns as Blob (handles HTTP URLs, data URIs, and Blob URLs)
   * @param {string} url - The PDF URL, data URI, or Blob URL
   * @returns {Promise<{data: Blob}>} - Response with blob data
   */
  downloadPDFAsBlob: async (url) => {
    
    try {
      const fileType = PDFResource.getFileType(url);
 
      // Clean up the URL
      const cleanUrl = String(url).trim().replace(/^["']|["']$/g, '');
 
      // Check what type of URL we have
      const isDataURI = PDFResource.isDataURI(cleanUrl);
      const isBlobURL = cleanUrl.startsWith('blob:');
      const isHTTP = cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://');

 
      // ====== CASE 1: Data URI ======
      if (isDataURI) {
        
        // Extract MIME type from data URI
        const mimeMatch = cleanUrl.match(/data:([^;]+);/);
        const detectedMime = mimeMatch ? mimeMatch[1] : null;
        
        // Determine correct MIME type
        let mimeType 
        if (detectedMime && detectedMime !== "text/plain" && !detectedMime.includes('text/plain' && !detectedMime.includes('text/'))){
          mimeType = detectedMime;
        } else if (fileType === 'excel'){
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        }else {
          mimeType = 'application/pdf';
        }
        
        // Critical check
        if(mimeType.includes('text/plain') || mimeType.includes('text/')){
          console.error ("Critical mimetype is text - thsi is calus .txt file ")
          console.error("Forcing mimeType base on fileType")
          mimeType = fileType === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'  : 'application/pdf'
        }
        if (!mimeType.includes('pdf') && !mimeType.includes('spreadsheet') && !mimeType.includes('excel')) {
          console.error("❌ CRITICAL: Invalid MIME type detected:", mimeType);
          console.error("🔴 THIS COULD CREATE .TXT FILE");
          console.error("File type was:", fileType);
        }
 
        // Convert data URI to Blob directly (NO AXIOS - avoids CSP issues)
        const blob = PDFResource.base64ToBlob(cleanUrl, mimeType);
         if(blob.type === '' || blob.type === 'text/plain' ){
          console.error("Criitical blob has invalid type after creation ")
          throw new Error ("Blob type validation failed")
         }
 
        // Return in same format as axios response
        const response = {
          data: blob,
          status: 200,
          statusText: 'OK',
          headers: {
            'content-type': mimeType
          },
          config: { url: cleanUrl.substring(0, 100) + "..." }
        };
        return response;
      }
 
      // ====== CASE 2: Blob URL ======
      if (isBlobURL) {
        
        const response = await fetch(cleanUrl);
        
        const blob = await response.blob();
 
        // Ensure we have a proper Blob
        if (!(blob instanceof Blob)) {
          console.error("❌ CRITICAL: Response is not a Blob");
          const contentType = fileType === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
          throw new Error("Received empty PDF blob");
        }
        
        // Check blob type
        if (!blob.type.includes('pdf') && !blob.type.includes('spreadsheet') && !blob.type.includes('excel')) {
          console.error("❌ WARNING: Blob type may be incorrect:", blob.type);
          console.error("🔴 THIS COULD CREATE .TXT FILE");
          const correctType = fileType === 'excel'?'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
          const fixedBlob = new Blob([blob],{type:correctType})
          const finalResponse = {
            ...response,
            data: fixedBlob
          };
          return finalResponse
        }
 
        const finalResponse = {
          data: blob,
          status: response.status,
          statusText: response.statusText,
          headers: {
            'content-type': blob.type
          },
          config: { url: cleanUrl.substring(0, 100) + "..." }
        };
        return finalResponse;
      }
 
      // ====== CASE 3: HTTP/HTTPS URL ======
      if (isHTTP) {
        
        const acceptHeader = fileType === 'excel' ?
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/pdf' :
          'application/pdf';
        
        
        const response = await axios({
          method: "get",
          url: cleanUrl,
          responseType: "blob",
          headers: {
            'Accept': acceptHeader
          },
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          }
        });
 
        let blobData = response.data;
 
 // Ensure we have a proper Blob
        if (!(blobData instanceof Blob)) {
          console.warn("⚠️ Response data is not a Blob, converting...");
          const contentType = fileType === 'excel' ?
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
            'application/pdf';
          blobData = new Blob([response.data], { type: contentType });
        }
        
 
        if (blobData.size === 0) {
          console.error("❌ CRITICAL: Empty blob received");
          throw new Error("Received empty PDF blob");
        }
        
        // Check blob type
        if (!blobData.type.includes('pdf') && !blobData.type.includes('spreadsheet') && !blobData.type.includes('excel') && !blobData.type.includes('octet-stream')) {
          console.error("❌ WARNING: Blob type may be incorrect:", blobData.type);
          console.error("🔴 THIS COULD CREATE .TXT FILE");
          console.error("Expected type based on fileType:", fileType);

          const correctType = fileType === 'excel'? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
            'application/pdf'; 
          blobData = new Blob([blobData],{type:correctType})
        } 
 
        const finalResponse = {
          ...response,
          data: blobData
        };
        
        return finalResponse;
      }
 
      // ====== CASE 4: Unknown URL type ======
      console.error("❌ CASE 4: Unknown URL type");
      console.error("URL does not match any known pattern:");
      console.error("URL:", cleanUrl.substring(0, 100) + "...");
      throw new Error(`Unsupported URL type: ${cleanUrl.substring(0, 50)}...`);
 
    } catch (error) {
      console.error("Error name:", error.name);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
 
      // Provide helpful error messages
      if (error.message === "Network Error") {
        console.error("🔴 Network Error - This usually means:");
        console.error("   1. CSP blocking the request");
        console.error("   2. CORS issue");
        console.error("   3. Data URI being passed to axios");
        console.error("   Solution: Use Blob URL instead of data URI");
      }
      
      if (error.message.includes("Failed to fetch")) {
        console.error("🔴 Fetch Error - Possible causes:");
        console.error("   1. Network connectivity issue");
        console.error("   2. Invalid blob URL");
        console.error("   3. Blob URL already revoked");
      }
 
      throw error;
    }
  },
 
  /**
   * convert excel blob to preview-friendly format
   */
  prepareExcelPreview: (blob) => {
    
    try {
      const blobUrl = window.URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      console.error("❌ Error preparing Excel preview:", error);
      return null;
    }
  },
 
  /**
   * Helper function to detect iOS devices
   * @returns {boolean} - True if iOS device
   */
  isIOS: () => {
    
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
 
    // Check for iPad, iPhone, iPod
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
 
    // Check for iPad on iOS 13+ (reports as MacIntel)
    const isIPadPro = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
 
    const result = isIOSDevice || isIPadPro;
 
    return result;
  },
 
  /**
   * Helper function to check msSaveOrOpenBlob support
   * @returns {boolean} - True if supported
   */
  supportsMsSaveBlob: () => {
    const supported = typeof window.navigator.msSaveOrOpenBlob !== 'undefined';
    return supported;
  }
};
 
export default PDFResource;